import { SynonymGroup } from "@/models/synonym-group/synonymGroup";
import { useItemService } from "./useServices";
import { useItemsCrud } from "./useItemsCrud";
import { PATH_KEYS } from "@/services/appServices";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { restError } from "@/models/client/RestResponse";

const useSynonymGroups = () => {
  const queryClient = useQueryClient();

  /**** Services ****/
  const synonymGroupService = useItemService(
    (services) => services.synonymGroup
  );

  const synonymGroupCrud = useItemsCrud<SynonymGroup>(
    [PATH_KEYS.synonymGroup],
    (services) => services.synonymGroup
  );

  /**** Queries & Mutations ****/

  const useQueryUserDictionary = () =>
    useQuery<SynonymGroup[]>({
      queryKey: [PATH_KEYS.synonymGroup, "active-synonym-dict"],
      queryFn: async () => {
        const response = await synonymGroupService.getUserDictionary();
        if (response.success) return response.value;
        else throw restError(response);
      },
      staleTime: 60_000,
    });

  const addSynonym = useMutation<
    SynonymGroup,
    string,
    { groupId: string; synonym: string }
  >({
    mutationKey: [PATH_KEYS.synonymGroup, "add"],
    mutationFn: async ({
      groupId,
      synonym,
    }: {
      groupId: string;
      synonym: string;
    }) => {
      const response = await synonymGroupService.addSynonym(groupId, synonym);
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.synonymGroup] });
    },
  });

  const removeSynonym = useMutation<
    SynonymGroup,
    string,
    { groupId: string; synonym: string }
  >({
    mutationKey: [PATH_KEYS.synonymGroup, "remove"],
    mutationFn: async ({
      groupId,
      synonym,
    }: {
      groupId: string;
      synonym: string;
    }) => {
      const response = await synonymGroupService.removeSynonym(
        groupId,
        synonym
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.synonymGroup] });
    },
  });

  const renameCanonicalTerm = useMutation<
    SynonymGroup,
    string,
    { groupId: string; canonicalTerm: string }
  >({
    mutationKey: [PATH_KEYS.synonymGroup, "rename"],
    mutationFn: async ({
      groupId,
      canonicalTerm,
    }: {
      groupId: string;
      canonicalTerm: string;
    }) => {
      const response = await synonymGroupService.renameCanonicalTerm(
        groupId,
        canonicalTerm
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.synonymGroup] });
    },
  });

  const activate = useMutation<
    SynonymGroup,
    string,
    { groupId: string; activateGroup: boolean; synonym: string | undefined }
  >({
    mutationKey: [PATH_KEYS.synonymGroup, "activate"],
    mutationFn: async ({
      groupId,
      activateGroup,
      synonym,
    }: {
      groupId: string;
      activateGroup: boolean;
      synonym: string | undefined;
    }) => {
      const response = await synonymGroupService.activate(
        groupId,
        activateGroup,
        synonym
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.synonymGroup] });
    },
  });

  const deactivate = useMutation<
    SynonymGroup,
    string,
    { groupId: string; deactivateGroup: boolean; synonym: string | undefined }
  >({
    mutationKey: [PATH_KEYS.synonymGroup, "deactivate"],
    mutationFn: async ({
      groupId,
      deactivateGroup,
      synonym,
    }: {
      groupId: string;
      deactivateGroup: boolean;
      synonym: string | undefined;
    }) => {
      const response = await synonymGroupService.deactivate(
        groupId,
        deactivateGroup,
        synonym
      );
      if (response.success) return response.value;
      else throw restError(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PATH_KEYS.synonymGroup] });
    },
  });

  /**** Value ****/
  return {
    ...synonymGroupCrud,
    useQueryUserDictionary,
    addSynonym,
    removeSynonym,
    renameCanonicalTerm,
    activate,
    deactivate,
  };
};

export default useSynonymGroups;
